import React from "react";
import { ContextEdit } from "../../../CommonLandingMweb/shared/MwebContextEdit";
import { useTravellerDetailsContext } from "../../contexts/TravellerDetailsV2Context";

type TravelDates = { startDate?: number; endDate?: number };

type CountryListEntry = { code?: string; name?: string; type?: string };

/** myPartner `COMPONENT_MYPARTNER_TRAVELLER_ATTRIBUTE_CONTEXT_EDIT` travelDetails shape. */
export interface TripContextTravelDetailsV2 {
  countries?: {
    title?: string;
    displayData?: string;
    countryCodes?: string[];
    isEditable?: boolean;
    countryList?: { value?: CountryListEntry[] };
  };
  dates?: {
    startDate?: number;
    endDate?: number;
    isEditable?: boolean;
    config?: { durationAllowed?: number };
  };
  travellerCount?: { title?: string; value?: number };
}

/** Legacy `COMPONENT_MYPARTNER_TRAVELLER_ATTRIBUTE_ITINERARY_INFO` shape. */
export interface TripContextLegacyTravelDetails {
  countries?: string;
  travelDates?: TravelDates;
  travellerCount?: number;
}

export type TripContextHeaderTravelDetails =
  | TripContextTravelDetailsV2
  | TripContextLegacyTravelDetails;

function isLegacyTripContextDetails(
  td: TripContextHeaderTravelDetails | undefined
): td is TripContextLegacyTravelDetails {
  return typeof (td as TripContextLegacyTravelDetails)?.countries === "string";
}

type NormalizedTravelDetails = {
  countries: {
    displayData: string;
    countryCodes: string[];
    isEditable?: boolean;
    title?: string;
    countryListValue: CountryListEntry[];
  };
  dates: {
    startDate?: number;
    endDate?: number;
    isEditable?: boolean;
    config?: { durationAllowed?: number };
  };
  travellerCount: { title?: string; value: number };
};

function normalizeTravelDetails(
  td: TripContextHeaderTravelDetails
): NormalizedTravelDetails {
  if (isLegacyTripContextDetails(td)) {
    return {
      countries: {
        displayData: td.countries ?? "",
        countryCodes: [],
        isEditable: true,
        title: undefined,
        countryListValue: [],
      },
      dates: {
        startDate: td.travelDates?.startDate,
        endDate: td.travelDates?.endDate,
        isEditable: true,
        config: undefined,
      },
      travellerCount: {
        title: undefined,
        value: typeof td.travellerCount === "number" ? td.travellerCount : 0,
      },
    };
  }
  const v2 = td;
  return {
    countries: {
      displayData: v2.countries?.displayData ?? "",
      countryCodes: v2.countries?.countryCodes ?? [],
      isEditable: v2.countries?.isEditable,
      title: v2.countries?.title,
      countryListValue: v2.countries?.countryList?.value ?? [],
    },
    dates: {
      startDate: v2.dates?.startDate,
      endDate: v2.dates?.endDate,
      isEditable: v2.dates?.isEditable,
      config: v2.dates?.config,
    },
    travellerCount: {
      title: v2.travellerCount?.title,
      value: v2.travellerCount?.value ?? 0,
    },
  };
}

export interface TripContextHeaderProps {
  heading?: string;
  travelDetails?: TripContextHeaderTravelDetails;
  cta?: { collapsedView?: string; expandedView?: string };
}

export const TripContextHeader = ({
  heading,
  travelDetails,
  cta,
}: TripContextHeaderProps) => {
  const { onBack, scrollElevated } = useTravellerDetailsContext();

  if (!travelDetails) return null;

  const n = normalizeTravelDetails(travelDetails);

  return (
    <ContextEdit
      heading={heading}
      travelDetails={{
        countries: {
          displayData: n.countries.displayData,
          countryCodes: n.countries.countryCodes,
          title: n.countries.title,
          isEditable: n.countries.isEditable,
          countryList: { value: n.countries.countryListValue },
        },
        dates: {
          startDate: n.dates.startDate,
          endDate: n.dates.endDate,
          isEditable: n.dates.isEditable,
          config: n.dates.config,
        },
        travellerCount: {
          title: n.travellerCount.title,
          value: n.travellerCount.value,
        },
      }}
      cta={cta}
      onBackPress={onBack}
      scrollElevated={scrollElevated}
      onCollapsedRowPress={onBack}
      confirmNavigationMode="traveller"
    />
  );
};
